import { useMemo } from 'react';
import { useModal } from '../../../helpers/hooks/modalContext';
import { TransactionType, type RewardsHistoryItem } from '../../../helpers/utils/types';
import { formatPoints } from '../utils/redeemUtils';

type WalletHistorySectionProps = {
  loading: boolean;
  items: RewardsHistoryItem[];
};

const HomeIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z' />
    <polyline points='9 22 9 12 15 12 15 22' />
  </svg>
);

const StarIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2' />
  </svg>
);

const GiftIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <polyline points='20 12 20 22 4 22 4 12' />
    <rect x='2' y='7' width='20' height='5' />
    <line x1='12' y1='22' x2='12' y2='7' />
    <path d='M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z' />
    <path d='M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z' />
  </svg>
);

const getHistoryVisual = (item: RewardsHistoryItem) => {
  const code = `${item.pointTaskCode}${item.pointRewardTypeCode}`.toLowerCase();

  if (code.includes('gift')) {
    return { iconClass: 'gray', icon: <GiftIcon />, titleClass: '' };
  }

  if (code.includes('drop') || code.includes('bonus') || code.includes('draw')) {
    return { iconClass: 'gold', icon: <StarIcon />, titleClass: 'rd-ph-title-gold' };
  }

  return { iconClass: 'blue', icon: <HomeIcon />, titleClass: '' };
};

const getStatusChip = (item: RewardsHistoryItem) => {
  if (item.transactionType !== TransactionType.Credit) return null;

  const code = `${item.pointTaskCode}${item.pointRewardTypeCode}`.toLowerCase();
  const isBonus = code.includes('drop') || code.includes('bonus') || code.includes('draw');

  if (isBonus) {
    return <span className='rd-ph-badge earned'>Earned</span>;
  }

  return <span className='rd-ph-badge confirmed'>Confirmed</span>;
};

export const WalletHistorySection = ({ loading, items }: WalletHistorySectionProps) => {
  const { setModal } = useModal();

  const recentItems = items.slice(0, 4);

  const summary = useMemo(() => {
    let earnedPoints = 0;
    let redeemedPoints = 0;
    let earnedTickets = 0;
    let redeemedTickets = 0;

    for (const item of items) {
      const amount = Number(item.amount);
      const isPoints = item.assetType === 'points';
      const isTickets = item.assetType === 'tickets';

      if (item.transactionType === TransactionType.Credit) {
        if (isPoints) earnedPoints += amount;
        if (isTickets) earnedTickets += amount;
      }
      if (item.transactionType === TransactionType.Debit) {
        if (isPoints) redeemedPoints += amount;
        if (isTickets) redeemedTickets += amount;
      }
    }

    return {
      earnedPoints,
      redeemedPoints,
      netPoints: earnedPoints - redeemedPoints,
      earnedTickets,
      redeemedTickets,
      netTickets: earnedTickets - redeemedTickets,
    };
  }, [items]);

  return (
    <div className='rd-panel-light'>
      <div className='rd-sec'>
        <div>
          <h2 className='rd-sec-title'>Points and tickets history</h2>
          <p className='rd-sec-sub'>Recent activity.</p>
        </div>
        <button
          type='button'
          className='rd-view-all'
          onClick={() => setModal('rewards', { openTab: 'history' })}
        >
          View all
        </button>
      </div>

      <div className='rd-ph'>
        {loading ? (
          <div className='rd-ph-empty'>Loading points and tickets history...</div>
        ) : recentItems.length === 0 ? (
          <div className='rd-ph-empty'>No points and tickets history available yet.</div>
        ) : (
          recentItems.map((item) => {
            const visual = getHistoryVisual(item);
            const isCredit = item.transactionType === TransactionType.Credit;
            const isDebit = item.transactionType === TransactionType.Debit;
            const amountClass = isCredit ? 'green' : isDebit ? 'red' : 'gray';
            const amountPrefix = isCredit ? '+' : isDebit ? '−' : '';
            const title =
              item.pointTaskDescription ||
              item.pointRewardTypeDescription ||
              'Points activity';
            const assetLabel = item.assetType === 'tickets' ? 'tickets' : 'pts';

            return (
              <div key={item.pointTransactionId} className='rd-ph-row'>
                <div className={`rd-ph-ic ${visual.iconClass}`}>{visual.icon}</div>
                <div className='rd-ph-main'>
                  <div className={`rd-ph-title ${visual.titleClass}`.trim()}>{title}</div>
                  <div className='rd-ph-date'>
                    {new Date(item.timestampAdded).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                </div>
                <div className='rd-ph-right'>
                  <div className={`rd-ph-amt ${amountClass}`}>
                    {amountPrefix}
                    {formatPoints(Number(item.amount))} {assetLabel}
                  </div>
                  {getStatusChip(item)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {!loading && items.length > 0 && (
        <div className='rd-ph-summary'>
          <div className='rd-ph-summary-item'>
            <span className='rd-ph-summary-lbl'>Total earned</span>
            <span className='rd-ph-summary-val green'>+{formatPoints(summary.earnedPoints)} pts</span>
          </div>
          <div className='rd-ph-summary-sep' />
          <div className='rd-ph-summary-item'>
            <span className='rd-ph-summary-lbl'>Total redeemed</span>
            <span className='rd-ph-summary-val red'>{summary.redeemedPoints > 0 ? '−' : ''}{formatPoints(summary.redeemedPoints)} pts</span>
          </div>
          <div className='rd-ph-summary-sep' />
          <div className='rd-ph-summary-item'>
            <span className='rd-ph-summary-lbl'>Net balance</span>
            <span className='rd-ph-summary-val'>
              {summary.netPoints < 0 ? '−' : ''}
              {new Intl.NumberFormat('en-US').format(Math.round(Math.abs(summary.netPoints)))} pts
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
